import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    Activity, ShoppingCart, BanknoteArrowUp, HandCoins, Send, ArrowDownCircle,
    ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../features/helpers";
import { fetchLatest } from "../../store/ReceiptsSlice";

import {
    PageCard, SectionTitle, PillTabs,
    DataList, DataListColumn,
    StatusBadge, MoneyValue,
} from "../ui";

import "./LatestActivityCard.scss";

const LIMIT = 10;

// =====================================================
// Tabs: 5 categorias mapeadas aos 5 selectors do receipts slice.
// =====================================================
type TabKey = "transactions" | "purchases" | "withdrawals" | "payments" | "shipments" | "deposits";

interface TabConfig {
    key: TabKey;
    label: string;
    icon: any;
    /** key utilizada pelo fetchLatest(entity) */
    apiEntity: string;
    /** selector do store */
    selector: (state: any) => any;
}

const TABS: TabConfig[] = [
    { key: "transactions", label: "Transacoes", icon: Activity,         apiEntity: "all",       selector: (s) => s.receipts.latestTransactions },
    { key: "purchases",    label: "Compras",    icon: ShoppingCart,     apiEntity: "purchases", selector: (s) => s.receipts.latestPurchases },
    { key: "withdrawals",  label: "Saques",     icon: BanknoteArrowUp,  apiEntity: "invoices",  selector: (s) => s.receipts.latestInvoices },
    { key: "payments",     label: "Pagamentos", icon: HandCoins,        apiEntity: "payments",  selector: (s) => s.receipts.latestPayments },
    { key: "shipments",    label: "Envios",     icon: Send,             apiEntity: "shipments", selector: (s) => s.receipts.latestShipments },
    { key: "deposits",     label: "Depositos",  icon: ArrowDownCircle,  apiEntity: "deposits",  selector: (s) => s.receipts.latestDeposits },
];

// =====================================================
// Helpers de mapeamento de campo por tipo (cada entidade
// tem nome de campo um pouco diferente — descricao curta).
// =====================================================
const describeRow = (key: TabKey, row: any): string => {
    if (!row) return "";

    if (key === "transactions") {
        // só o nome do cliente — a categoria vai no sub-texto apagado (categoryOf)
        if (row.purchases?.length > 0)  return row.purchases[0].target_user?.name ?? "—";
        if (row.payments?.length > 0)   return row.payments[0].target?.name ?? "—";
        if (row.deposits?.length > 0)   return row.deposits[0].target?.name ?? row.deposits[0].wallet?.user?.name ?? "—";
        if (row.invoices?.length > 0)   return row.invoices[0].user?.name ?? "—";
        return row.type ?? "—";
    }

    if (key === "purchases")   return row.target_user?.name ?? row.asset?.name ?? "—";
    if (key === "withdrawals") return row.asset?.name ?? row.target?.name ?? "—";
    if (key === "payments")    return row.target?.name ?? "—";
    if (key === "shipments")   return row.target?.name ?? row.userFromWalletName ?? "—";
    if (key === "deposits")    return row.target?.name ?? row.wallet?.user?.name ?? row.userFromWalletName ?? "—";

    return "—";
};

// Nome da categoria (PT) exibido de forma discreta sob o nome do cliente.
const CATEGORY_LABEL: Record<TabKey, string> = {
    transactions: "Transação",
    purchases:    "Compra",
    withdrawals:  "Saque",
    payments:     "Pagamento",
    shipments:    "Envio",
    deposits:     "Depósito",
};

const categoryOf = (key: TabKey, row: any): string => {
    if (key === "transactions") {
        if (row.purchases?.length) return "Compra";
        if (row.payments?.length)  return "Pagamento";
        if (row.deposits?.length)  return "Depósito";
        if (row.invoices?.length)  return "Envio / Saque";
        return "Transação";
    }
    return CATEGORY_LABEL[key];
};

const directionOf = (row: any): "up" | "down" | "flat" => {
    if (row?.direction === "IN")  return "up";
    if (row?.direction === "OUT") return "down";
    const n = Number(row?.value ?? 0);
    if (n > 0)  return "up";
    if (n < 0)  return "down";
    return "flat";
};

// =====================================================
// Component
// =====================================================
const LatestActivityCard: FunctionComponent = () => {

    const dispatch = useAppDispatch();
    const [tab, setTab] = useState<TabKey>("transactions");

    // Carrega TODAS as listas no mount; depois cada tab so seleciona.
    useEffect(() => {
        TABS.forEach((t) => dispatch(fetchLatest(t.apiEntity)));
    }, [dispatch]);

    const currentConfig = TABS.find((t) => t.key === tab)!;
    const rawData = useAppSelector(currentConfig.selector);

    // Pega o timestamp mais confiavel pra ordenacao.
    const rowTimestamp = (row: any): number => {
        const raw = row?.updatedAt ?? row?.createdAt ?? row?.date ?? 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
    };

    // Ordena por data desc e limita a LIMIT itens.
    const rows = useMemo(() => {
        const arr = [...(rawData?.data ?? [])];
        arr.sort((a, b) => rowTimestamp(b) - rowTimestamp(a));
        return arr.slice(0, LIMIT);
    }, [rawData]);

    const totalAvailable = rawData?.total ?? rows.length;

    const isLoading = !rawData;

    // ---- Colunas comuns aos 5 tipos
    const columns: DataListColumn<any>[] = useMemo(() => {
        return [
            {
                key: "desc",
                header: "Descricao",
                width: "2fr",
                render: (row) => {
                    const dir = directionOf(row);
                    return (
                        <div className="lac-desc">
                            <span className={`lac-dir dir-${dir}`}>
                                {dir === "up"   && <ArrowUpRight size={14} />}
                                {dir === "down" && <ArrowDownRight size={14} />}
                                {dir === "flat" && <Send size={12} />}
                            </span>
                            <div className="lac-desc-text">
                                <strong>{describeRow(tab, row)}</strong>
                                <small className="lac-category">{categoryOf(tab, row)}</small>
                            </div>
                        </div>
                    );
                },
            },
            {
                key: "value",
                header: "Valor",
                align: "right",
                render: (row) => <MoneyValue amount={row.value} tone="auto" />,
                mono: true,
            },
            {
                key: "date",
                header: "Data",
                align: "right",
                width: "190px",
                render: (row) => (
                    <span style={{ opacity: .65, whiteSpace: "nowrap" }} className="num">
                        {row.updatedAt ?? row.createdAt ?? "—"}
                    </span>
                ),
            },
            {
                key: "status",
                header: "Status",
                width: "130px",
                render: (row) => <StatusBadge status={row.status} />,
            },
        ];
    }, [tab]);

    return (
        <PageCard className="latest-activity-card">
            <SectionTitle
                icon={Activity}
                count={totalAvailable}
                actions={
                    <PillTabs
                        ariaLabel="Tipo de movimentacao"
                        value={tab}
                        onChange={(k: string) => setTab(k as TabKey)}
                        collapseOnMobile
                        tabs={TABS.map((t) => ({ key: t.key, label: t.label, icon: t.icon }))}
                    />
                }
            >
                Ultimas movimentacoes
            </SectionTitle>

            <DataList
                columns={columns}
                rows={rows}
                loading={isLoading}
                empty={`Sem ${currentConfig.label.toLowerCase()} no momento.`}
                rowKey={(row, i) => row.id ?? row.trx_id ?? i}
            />
        </PageCard>
    );
};

export default LatestActivityCard;
